import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  syncHyperliquidData, 
  getDashboardData, 
  exportData, 
  downloadBlob,
  type DashboardData,
  type SyncResult,
  type MarketType
} from '@/lib/hyperliquid';

interface UseDashboardDataOptions {
  address: string | undefined;
  rangeDays?: number;
  marketType?: MarketType;
}

interface UseDashboardDataReturn {
  data: DashboardData | null;
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastSynced: Date | null;
  sync: (captchaToken?: string) => Promise<SyncResult | null>;
  refresh: () => Promise<void>;
  exportCsv: (type: 'fills' | 'daily' | 'portfolio') => Promise<void>;
  rangeDays: number;
  setRangeDays: (days: number) => void;
}

export function useDashboardData({ 
  address, 
  rangeDays: initialRangeDays = 30,
  marketType = 'overview'
}: UseDashboardDataOptions): UseDashboardDataReturn {
  const { toast } = useToast();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [rangeDays, setRangeDays] = useState(initialRangeDays);

  const fetchDashboard = useCallback(async () => {
    if (!address) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const dashboardData = await getDashboardData(address, rangeDays, marketType);
      setData(dashboardData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch dashboard data';
      setError(message);
      console.error('Dashboard fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [address, rangeDays, marketType]);

  const sync = useCallback(async (captchaToken?: string): Promise<SyncResult | null> => {
    if (!address) return null;
    
    setIsSyncing(true);
    setError(null);
    
    try {
      const result = await syncHyperliquidData(address, rangeDays, captchaToken, marketType);
      setLastSynced(new Date());
      
      toast({
        title: 'Sync complete',
        description: `Fills: ${result.fillsFetched} • Pages: ${result.pages} • Portfolio points: ${result.portfolioPointsStored} • Window: ${result.portfolioWindowUsed ?? 'n/a'}`,
      });
      
      // Refresh dashboard after sync
      await fetchDashboard();
      
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      setError(message);
      toast({
        title: 'Sync failed',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [address, rangeDays, marketType, fetchDashboard, toast]);

  const refresh = useCallback(async () => {
    await fetchDashboard();
  }, [fetchDashboard]);

  const exportCsv = useCallback(async (type: 'fills' | 'daily' | 'portfolio') => {
    if (!address) return;
    
    try {
      const blob = await exportData(address, rangeDays, type, marketType);
      const filename = `hyperliquid-${type}-${address.slice(0, 8)}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadBlob(blob, filename);
      
      toast({
        title: 'Export complete',
        description: `Downloaded ${filename}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      toast({
        title: 'Export failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [address, rangeDays, marketType, toast]);

  // Fetch dashboard data when address or range changes
  useEffect(() => {
    if (address) {
      fetchDashboard();
    }
  }, [address, rangeDays, fetchDashboard]);

  return {
    data,
    isLoading,
    isSyncing,
    error,
    lastSynced,
    sync,
    refresh,
    exportCsv,
    rangeDays,
    setRangeDays,
  };
}
